import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

/** Padding around the paper inside the dark canvas. */
export const CANVAS_PAD = 16;
/** Gap between the pages, matching the preview's own flex gap. */
export const PAGE_GAP = 16;
/** How far the paper may be blown up past its printed size. */
const MAX_SCALE = 1.6;
/** …and how far down it may go before the type stops being readable. */
const MIN_SCALE = 0.45;

/**
 * Zoom steps, as a multiple of the fitted scale. 1 is "the whole page fits",
 * which is where the preview opens and what Reset goes back to.
 */
const ZOOM_STEPS = [0.75, 1, 1.5, 2, 3, 4];
const FIT_INDEX = ZOOM_STEPS.indexOf(1);

/**
 * The zoom level a preview is being read at, as a multiple of its fitted size.
 *
 * It lives outside the frame because the buttons that drive it sit in the
 * panel header, which the page owns — the frame only reads the number.
 */
export function usePageZoom() {
    const [index, setIndex] = useState(FIT_INDEX);

    const zoomIn = useCallback(() => setIndex((i) => Math.min(ZOOM_STEPS.length - 1, i + 1)), []);
    const zoomOut = useCallback(() => setIndex((i) => Math.max(0, i - 1)), []);
    const reset = useCallback(() => setIndex(FIT_INDEX), []);

    return useMemo(() => ({
        zoom: ZOOM_STEPS[index],
        zoomIn,
        zoomOut,
        reset,
        canZoomIn: index < ZOOM_STEPS.length - 1,
        canZoomOut: index > 0,
        isFitted: index === FIT_INDEX,
    }), [index, zoomIn, zoomOut, reset]);
}

/**
 * Fit a printed page to the preview card it is shown in, then apply the
 * reader's own zoom on top.
 *
 * The fitted scale is whichever of the two axes runs out first, so at zoom 1 a
 * whole page is on screen at once — a form preview you have to scroll to see
 * the foot of page one is no use for checking that the answers landed on the
 * right lines. What scrolls is the *next* page, not the rest of this one.
 *
 * Zoomed in past the card, the paper is dragged around rather than scrolled:
 * the returned ref also wires up press-and-drag panning.
 *
 * @param {number} pageWidth Printed page width in pt.
 * @param {number} pageHeight Printed page height in pt.
 * @param {number} zoom Multiple of the fitted scale (see `usePageZoom`).
 * @param {number} pageCount How many pages the document has.
 * @returns {[import('react').RefObject<HTMLElement>, number, number]} the scroll
 *   box ref to attach, the scale to draw the paper at, and which page is being
 *   looked at.
 */
export function usePagePreviewScale(pageWidth, pageHeight, zoom = 1, pageCount = 1) {
    const ref = useRef(null);
    const [fitted, setFitted] = useState(1);
    const [page, setPage] = useState(1);

    useEffect(() => {
        const el = ref.current;
        if (!el || typeof ResizeObserver === 'undefined') return undefined;

        const measure = () => {
            const availableWidth = el.clientWidth - CANVAS_PAD * 2;
            const availableHeight = el.clientHeight - CANVAS_PAD * 2;
            if (availableWidth <= 0 || availableHeight <= 0) return;
            setFitted(Math.max(MIN_SCALE, Math.min(
                MAX_SCALE,
                availableWidth / pageWidth,
                availableHeight / pageHeight,
            )));
        };

        measure();
        const observer = new ResizeObserver(measure);
        observer.observe(el);
        return () => observer.disconnect();
    }, [pageWidth, pageHeight]);

    // Which page is in front of the reader, so the preview can say "1 / 2" —
    // without it there is nothing on screen to suggest a second page is down
    // there at all. The page whose body covers the middle of the card wins.
    useEffect(() => {
        const el = ref.current;
        if (!el || pageCount < 2) return undefined;

        const step = (pageHeight + PAGE_GAP) * (fitted * zoom);
        const read = () => {
            const middle = el.scrollTop + el.clientHeight / 2;
            setPage(Math.min(pageCount, Math.max(1, Math.floor(middle / step) + 1)));
        };

        read();
        el.addEventListener('scroll', read, { passive: true });
        return () => el.removeEventListener('scroll', read);
    }, [pageCount, pageHeight, fitted, zoom]);

    // Back at fitted size there is nothing left to pan across, so the page the
    // reader was zoomed into is put back at its top — otherwise "fit whole
    // page" leaves them looking at the join between two of them.
    useEffect(() => {
        const el = ref.current;
        if (el && zoom === 1) el.scrollTo({ top: 0, left: 0 });
    }, [zoom]);

    // Press-and-drag panning. Pointer capture keeps the gesture alive when the
    // cursor leaves the box, and the grab cursor only appears once there is
    // something to drag — at fitted size the page does not move.
    useEffect(() => {
        const el = ref.current;
        if (!el) return undefined;

        let panning = false;
        let originX = 0;
        let originY = 0;
        let scrollX = 0;
        let scrollY = 0;

        const overflows = () => el.scrollWidth > el.clientWidth + 1 || el.scrollHeight > el.clientHeight + 1;

        const onPointerDown = (event) => {
            // Left button only, and never steal a text selection or a scrollbar drag.
            if (event.button !== 0 || !overflows()) return;
            panning = true;
            originX = event.clientX;
            originY = event.clientY;
            scrollX = el.scrollLeft;
            scrollY = el.scrollTop;
            el.setPointerCapture(event.pointerId);
            el.style.cursor = 'grabbing';
            event.preventDefault();
        };

        const onPointerMove = (event) => {
            if (!panning) return;
            el.scrollLeft = scrollX - (event.clientX - originX);
            el.scrollTop = scrollY - (event.clientY - originY);
        };

        const stop = (event) => {
            if (!panning) return;
            panning = false;
            try {
                el.releasePointerCapture(event.pointerId);
            } catch {
                /* the capture is already gone */
            }
            el.style.cursor = overflows() ? 'grab' : '';
        };

        el.style.cursor = overflows() ? 'grab' : '';
        el.addEventListener('pointerdown', onPointerDown);
        el.addEventListener('pointermove', onPointerMove);
        el.addEventListener('pointerup', stop);
        el.addEventListener('pointercancel', stop);

        return () => {
            el.removeEventListener('pointerdown', onPointerDown);
            el.removeEventListener('pointermove', onPointerMove);
            el.removeEventListener('pointerup', stop);
            el.removeEventListener('pointercancel', stop);
            el.style.cursor = '';
        };
    }, [zoom, fitted]);

    return [ref, fitted * zoom, page];
}
